import { Injectable, Injector, inject } from '@angular/core';
import {FirebaseApi} from '../../common/services/firebase-api.service';
import {ProjectData} from '../../common/model/project-data';
import {BehaviorSubject, Observable, Subscription} from 'rxjs';
import {AuthService} from '../../common/services/auth.service';
import firebase from 'firebase/compat/app';
import {ProgressCallback, ServerImportArrival, SyncService} from './sync.service';
import {everything} from '../models/clear-selection';
import {Title} from '@angular/platform-browser';
import {Session} from '../../common/model/session';
import {UserInfo} from '../../common/model/user-info';
import {Logger} from '../../common/utils/logger';
import {CommonUtil} from '../../common/utils/common.util';
import {embedUnavailableUrl} from '../../common/utils/embed-unavailable';
import {PresenceService} from '../../common/services/presence.service';
import {ConnectionStatus} from '../../common/enums/connection-status.enum';
import {ObjectUtil} from '../../common/utils/object.util';
import {MapService} from './map.service';
import {Router} from '@angular/router';
import {DialogResult} from '../../common/components/generic-dialog.popup';
import {StringsService} from '../../../../projects/common/src/lib/services/strings.service';
import {PopupsService} from './popups.service';
import {FileUtil} from '../../common/utils/file.util';
import {FileImport, FileLoader, ImportError} from '../file-loaders/file.loader';
import {Polygon} from '../models/polygon';
import {countedShapes, totalMeasuredArea} from '../utils/poly-util';
import {OrganizationService} from '../../common/services/organization.service';
import {MatSnackBar} from '@angular/material/snack-bar';
import {ERROR_SNACK_DURATION, openSnack} from '../../common/utils/snack.util';
import {ProjectUtils} from "../../common/utils/project-utils";
import {ProductsService} from './products.service';
import {DirectoryApiService} from '../../common/services/api/routes/directory-api.service';
import {AnalyticsEventsService} from '../../common/services/api/routes/events-api.service';
import {PendingImportService} from '../../common/services/pending-import.service';
import {
  ImportApiService, ImportBatchRequest, ImportBatchResponse, ImportMode, ImportPreview, ImportPreviewApiService, ImportStyle
} from '../../common/services/api/routes/import-api.service';
import {ImportBatchView, ImportJobsService} from './import-jobs.service';
import {GeometryConverter} from '../utils/geometry-converter';
import {GeometryType} from '../enums/geometry-type.enum';
import {GeoGroupData} from '../models/geo-group-data';
import {SettingsService} from './settings.service';

export enum LoadProjectStatus {
  SUCCESS = 'sucess',
  NOT_FOUND = 'not_found',
  ACCESS_DENIED = 'access_denied',
  ACCESS_DENIED_SUBSCRIPTION = 'access_denied_subscription',
  ERROR = 'error',
  /** Another project was opened, or this one left, before it finished loading: nothing to do. */
  SUPERSEDED = 'superseded'
}

/** A file an import could not bring in, and why. */
interface ImportFailure {
  file: File;
  error: any;
}

/** What an import of one or more files did, for the message after it. */
interface ImportSummary {
  /** Items brought in: shapes and spots, and the features of reference layers. */
  items: number;
  /** A data file was read and added, even if it held nothing to show. */
  imported: boolean;
  /** The map changed (cleared, or something added), so there is a step to undo. */
  changed: boolean;
  skipped: {[kind: string]: number};
  failed: ImportFailure[];
  /** What the server's import brought, and a Replace took away: recorded as the import's one step. */
  arrivals: ServerImportArrival[];
  /** A Replace whose clear of the old map failed after the import came in. */
  notCleared: boolean;
}

/** What one file the demo read brought. */
interface FileImportResult {
  items: number;
  skipped: {[kind: string]: number};
  groups: string[];
}

/** An import the server is running, as the page that started it follows it. */
interface ServerImportRun {
  projectId: string;
  batchId: string;
  /** The files, in the order of their jobs. */
  files: File[];
  /** What a Replace takes away, as it was when the import started: what its Undo puts back if nothing newer is known. */
  removing: any[];
  /** Uploads that failed here, with why: a better reason than the server's. */
  uploadErrors: Map<string, any>;
}

/** This page's uploads of one import: Stop cancels them. */
interface ImportUploads {
  stopped: boolean;
  task: firebase.storage.UploadTask | null;
}

/** The most a KMZ may weigh: its pictures are not parsed (the server has the same limit). */
const MAX_KMZ_BYTES = 500 * 1024 * 1024;
/** The most the demo's import takes: the file travels in the request (POST /import/preview). */
const MAX_PREVIEW_BYTES = 5 * 1024 * 1024;
const MB = 1024 * 1024;
/** How often an upload says how far it got: others see it, and that its page is still there. */
const UPLOAD_REPORT_MS = 15000;
/** An import done within this long fits the view to what came in; a longer one ends while the person works. */
const QUICK_IMPORT_MS = 30000;

@Injectable({
  providedIn: 'root'
})
export class ProjectService {
  private router = inject(Router);
  private injector = inject(Injector);
  private auth = inject(AuthService);
  private firebaseApi = inject(FirebaseApi);
  private importJobs = inject(ImportJobsService);
  /** This page's uploads, by import: Stop cancels them (stopImport). */
  private importUploads = new Map<string, ImportUploads>();
  private directoryApi = inject(DirectoryApiService);
  private syncService = inject(SyncService);
  private strings = inject(StringsService);
  private mapService = inject(MapService);
  private titleService = inject(Title);
  private snackBar = inject(MatSnackBar);
  private presenceService = inject(PresenceService);
  private analyticsEvents = inject(AnalyticsEventsService);
  private organization = inject(OrganizationService);
  private pendingImport = inject(PendingImportService);

  private activeProject: ProjectData;
  private projectSubject = new BehaviorSubject<ProjectData>(null);
  private onSessionLocked = new BehaviorSubject<boolean>(false);
  public onConnectedUsersChanged = new BehaviorSubject<UserInfo[]>([]);

  private conflictSes = 0;
  private currentSessionId: string;
  private projectSubscription: Subscription;
  private connectedUsers: UserInfo[];
  private sessionsSubscription: Subscription;
  private projects: ProjectData[];
  private listSummaryTimer: ReturnType<typeof setTimeout>;
  /** The project whose map finished loading in this canvas (see canWriteSummary). */
  private loadedProjectId: string = null;
  /** Counts project loads and closes: a load overtaken by either is dropped (see loadProject). */
  private loadToken = 0;

  constructor() {
    const auth = this.auth;
    const syncService = this.syncService;
    const presenceService = this.presenceService;


    auth.currentUserObservable.subscribe(user => {
      if (!user && !syncService.isDemoMode()) {
        this.currentProject = null;
      }
    });

    // The project list shows each project's shape counts and estimate, and
    // the map view places it by its bounds. They were only written when the
    // project was left inside the app, so closing the tab after drawing left
    // the list with no shapes and the project off the map. They now follow
    // the saved changes, a little after the last one. The picture is not
    // drawn here any more: the backend draws it, for every app, from the
    // changes it sees (functions' common/manager/project-thumbnails.ts).
    syncService.changesSavedEvent.subscribe(() => {
      clearTimeout(this.listSummaryTimer);
      this.listSummaryTimer = setTimeout(() => {
        if (this.currentProject && this.canEdit()) {
          this.updateProjectBounds();
          this.saveCounts();
        }
      }, 20000);
    });

    // Files still to upload stop with the page: leaving asks first, as for edits not yet saved.
    window.addEventListener('beforeunload', event => {
      if (this.importJobs.hasUploadsHere()) {
        event.preventDefault();
        event.returnValue = '';
      }
    });

    // This service subscribes before the canvas does, so the summary the
    // canvas writes when loading finishes already counts as from a loaded map.
    // A load that failed finishes with no count, and leaves an empty map.
    syncService.onLoadingFinished.subscribe(pointsCount => {
      if (typeof pointsCount === 'number' && this.currentProject) {
        this.loadedProjectId = this.currentProject.project_id;
        // A project made from a file on the dashboard: the file comes in now,
        // after the canvas has handled the load (it subscribes after this).
        const file = this.pendingImport.take(this.currentProject.project_id);
        if (file) {
          setTimeout(() => this.loadFiles([file], null));
        }
      }
    });
    // Emptied in this browser for a load, a reload or a raw data view: the
    // map counts as loaded again only when that load finishes. A version
    // restore whose reload failed left it counted as loaded while empty, and
    // leaving then wrote the empty map's summary over the project's.
    syncService.mapCleared.subscribe((event: {reset?: boolean}) => {
      if (event?.reset) {
        this.loadedProjectId = null;
      }
    });

    presenceService.observable.subscribe(status => {
      switch (status) {
        case ConnectionStatus.ONLINE:
          this.reconnectUser();
          break;
        case ConnectionStatus.OFFLINE:
        case ConnectionStatus.AWAY:
          this.disconnectUser();
          break;
      }
    });
  }

  set currentProject(project: ProjectData) {
    this.activeProject = project;
    this.syncService.currentProject = project;
    this.projectSubject.next(project);
  }

  get currentProject() {
    return this.activeProject;
  }

  get currentProjectId() {
    if (this.activeProject) {
      return this.activeProject.project_id;
    }
    return null;
  }

  get observe(): Observable<ProjectData> {
    return this.projectSubject.asObservable();
  }

  setProjects(projects: ProjectData[]) {
    this.projects = projects;
    Logger.log(projects);
  }

  loadProject(projectId: string, demoMode: boolean, progressCallback: ProgressCallback): Promise<LoadProjectStatus> {
    // Another project opened in the same canvas (a notification's link) leaves
    // this one first, as leaving the canvas does: collaborators stopped seeing
    // this account in it, and its list summary was not saved.
    if (this.currentProject && this.currentProject.project_id !== projectId) {
      this.closeProject();
    }
    this.loadedProjectId = null;
    // Another project opened, or this one left, while this one loaded: a slow
    // load used to open its project over the one opened after it.
    const token = ++this.loadToken;
    const overtaken = () => token !== this.loadToken;
    return new Promise<LoadProjectStatus>((resolve, reject) => {
      Logger.log('Getting Firebase Project');
      try {

        if (demoMode) {
          const proj = this.firebaseApi.newProject(null);
          // In the reader's language: it is the tab title and the canvas header.
          // The demo is never saved, so nothing stores the translated name.
          proj.name = this.strings.getString('demo_project_name');
          proj.project_id = 'test_demo';
          Logger.log('Demo Mode here', proj);
          this.initProjectSession(proj).then(() => {
            this.syncService.loadFromDatabase(proj, progressCallback).then(resolve);
          }).catch(reason => {
            Logger.error(reason);
            resolve(LoadProjectStatus.ERROR);
          });

          return;
        }

        const projectRef = this.firebaseApi.getProjectRef(projectId);
        projectRef.ref.get().then(async snapshot => {
          const proj = snapshot.data() as ProjectData;
          Logger.log('Loading Project: ', proj);
          if (!proj) {
            resolve(LoadProjectStatus.NOT_FOUND);
            return;
          }

          // An Admin or Manager's copy of the organisation arrives after
          // sign-in, and its member list decides whether they may open a
          // member's project (OrganizationService.isStaffFor).
          await this.organization.ready();
          if (overtaken()) {
            resolve(LoadProjectStatus.SUPERSEDED);
            return;
          }
          if (!this.canAccessProject(proj)) {
            resolve(LoadProjectStatus.ACCESS_DENIED);
            return;
          }

          // if (!this.auth.isGod() && !this.canAccessProject(proj)) {
          //   resolve(LoadProjectStatus.ACCESS_DENIED_SUBSCRIPTION);
          //   return;
          // }

          this.initProjectSession(proj).then(() => {
            if (overtaken()) {
              resolve(LoadProjectStatus.SUPERSEDED);
              return;
            }
            this.syncService.loadFromDatabase(proj, progressCallback).then(resolve);
          }).catch(reason => {
            Logger.error(reason);
            resolve(LoadProjectStatus.ERROR);
          });

        }).catch(error => {
          // The Promise was rejected.
          if (error.code === 'permission-denied') {
            Logger.error('PERMISSION DENIED HERE!!!!!');
            resolve(LoadProjectStatus.ACCESS_DENIED);
          } else {
            Logger.error(error);
            resolve(LoadProjectStatus.ERROR);
          }
        });


      } catch (err) {
        if (err.code === 'permission-denied') {
          Logger.error('PERMISSION DENIED HERE!!!!!');
          resolve(LoadProjectStatus.ACCESS_DENIED);
        } else {
          Logger.error(err);
          resolve(LoadProjectStatus.ERROR);
        }
      }
      // Not found, refused or failed, an overtaken load says nothing: the
      // canvas sent the person away from the project they had opened since.
    }).then(status => overtaken() ? LoadProjectStatus.SUPERSEDED : status);
  }

  initProjectSession(proj: ProjectData) {
    Logger.log(`initProjectSession - `, proj);
    this.currentProject = proj;

    this.titleService.setTitle(proj.name);
    this.initProjectEvents(proj.project_id);

    Logger.log(`initProjectSession 2`, this.canEdit());
    if (this.canEdit()) {
      Logger.log(`initProjectSession 3`);
      this.firebaseApi.getProjectRef(proj.project_id).update({
        last_open: Date.now()
      });

      this.reconnectUser();

      const sessionsRef = this.firebaseApi.getSessionsRef().query.ref;
      if (this.currentSessionId) {
        sessionsRef.child(this.currentSessionId).remove();
      }
      const sessionRef = sessionsRef.push();
      this.setSessionId(sessionRef.key);
      // let sessionSet = false;

      Logger.log(`initProjectSession 4`);
      const userId = this.firebaseApi.getUserId();

      return new Promise<void>((resolve, reject) => {
        sessionRef.set({
          isOnline: true,
          sessionId: this.currentSessionId,
          projectId: proj.project_id,
          timestamp: firebase.database.ServerValue.TIMESTAMP,
          uid: userId
        }).then(() => {
          // Left, or another project opened, while this session was being
          // written: it is taken out again, and the newer one keeps its watch.
          if (sessionRef.key !== this.currentSessionId) {
            sessionRef.remove().catch(err => Logger.error(err));
            resolve();
            return;
          }
          sessionRef.onDisconnect().remove();
          this.sessionsSubscription = this.firebaseApi.database
            .list<Session>(sessionsRef, ref => ref.orderByChild('uid').equalTo(userId)).valueChanges().subscribe(snapshot => {
              this.conflictSes = 0;
              if (snapshot.length > 0) {
                const now = new Date().getTime();
                // let toRemove = [];
                snapshot.forEach(ses => {
                  // const ses = snap.val();
                  if (ses.uid === ses.uid && ses.projectId === proj.project_id && now - ses.timestamp < 43200000) {
                    this.conflictSes++;
                  }
                });

                if (this.conflictSes > 2) {
                  this.lockSession();
                } else {
                  this.unlockSession();
                }

                this.dispatchOpenSessionsChanged(this.conflictSes);
                Logger.log(this.conflictSes + ' SESSIONS OPEN HERE!!!!');
                // toRemove.forEach(function () {
                //                     //
                //                     // })
              }
              resolve();
            });
        }).catch(reject);
      });
    } else {
      return Promise.resolve();
    }
  }

  private initProjectEvents(projectId: string) {
    this.resetConnectedUsers();
    if (this.sessionsSubscription) {
      this.sessionsSubscription.unsubscribe();
      this.sessionsSubscription = null;
    }
    if (this.projectSubscription) {
      this.projectSubscription.unsubscribe();
      this.projectSubscription = null;
    }
    Logger.log('initProjectEvents ', projectId);
    if (projectId && !this.syncService.isDemoMode()) {
      Logger.log('initProjectEvents 22 ', projectId);
      // this.connectedUsersSubscription = this.firebaseApi.getProjectRef(projectId).valueChanges().subscribe(newProject => {
      //   const currentUids = this.connectedUsers.map(u => u.uid);
      //   ObjectUtil.every(newProject.connected_users, (uid, obj) => {
      //     if (!currentUids.includes(uid)) {
      //       this.onUserConnected(uid);
      //     }
      //   });
      //   currentUids.forEach(uid => {
      //     if (!newProject.connected_users[uid]) {
      //       this.onUserDisconnected(uid);
      //     }
      //   });
      //
      // });


      this.projectSubscription = this.firebaseApi.getProjectRef(projectId).snapshotChanges().subscribe({
        next: snapshot => {
          Logger.log('projectSubscription', snapshot.type);
          // A deleted project arrives as a snapshot without a document, whose
          // data() is undefined: the check for 'removed' used to sit behind a
          // check for data, and never ran.
          if (snapshot.type === 'removed' || !snapshot.payload.exists) {
            this.leaveUnavailableProject(projectId);
            return;
          }
          const newProject = snapshot.payload.data();
          if (!this.canAccessProject(newProject)) {
            this.leaveUnavailableProject(projectId);
            return;
          }
          if (this.canEdit()) {
            const connected = newProject.connected_users || {};
            const currentUids = this.connectedUsers.map(u => u.uid);
            ObjectUtil.every(connected, (uid, obj) => {
              if (!currentUids.includes(uid)) {
                this.onUserConnected(uid);
              }
            });
            currentUids.forEach(uid => {
              if (!connected[uid]) {
                this.onUserDisconnected(uid);
              }
            });
          }
          this.currentProject = newProject;
        },
        // Losing the right to read the project (deleted, or this account taken
        // off a private one) ends the listener with an error. Nothing handled
        // it, so the canvas stayed open on a project that refused every save.
        error: err => {
          Logger.error('Project listener stopped', projectId, err);
          if (err?.code === 'permission-denied') {
            this.leaveUnavailableProject(projectId);
          }
        }
      });
    }
  }

  private setSessionId(sessionId: string) {
    Logger.log('setSessionId: ', sessionId);
    this.currentSessionId = sessionId;
    this.firebaseApi.setSessionId(sessionId);
    this.syncService.currentSessionId = sessionId;
  }

  getSessionId(): string {
    return this.currentSessionId;
  }

  reconnectUser() {
    const uid = this.firebaseApi.getUserId();

    if (!uid || CommonUtil.inIframe() || !this.canEdit()) {
      return;
    }

    const proj = this.currentProject;
    if (proj) {
      const update = {};
      update[`connected_users.${uid}`] = Date.now();
      this.firebaseApi.getProjectRef(proj.project_id).ref
        .update(update)
        .then(() => {
          Logger.log(`Connected to Project ${proj.project_id}`);
        }).catch(err => {
        Logger.error(err);
      });
    }
  }

  disconnectUser() {
    const uid = this.firebaseApi.getUserId();

    if (!uid || CommonUtil.inIframe() || !this.canEdit()) {
      return;
    }

    const proj = this.currentProject;
    if (proj) {
      const update = {};
      update[`connected_users.${uid}`] = firebase.firestore.FieldValue.delete();
      this.firebaseApi.getProjectRef(proj.project_id).ref
        .update(update)
        .then(() => {
          Logger.log(`Disconnected from Project ${proj.project_id}`);
        }).catch(err => {
        Logger.error(err);
      });
    }
  }

  onUserConnected(uid: string) {
    const myUserId = this.firebaseApi.getUserId();
    if (myUserId === uid) {
      return;
    }
    // A collaborator's name and photo, from the API: their user document is
    // private to them.
    const projectId = this.currentProject?.project_id;
    this.directoryApi.lookupOne(uid, {projectId}).then(profile => {
      const userInfo = profile ? DirectoryApiService.asUserInfo(profile) : null;
      if (userInfo) {
        let found = false;
        this.connectedUsers.forEach(us => {
          if (us.uid === uid) {
            found = true;
          }
        });
        if (!found) {
          this.connectedUsers.push(userInfo);
          this.dispatchConnectedUsersChanged();
        }
      }
    });
  }

  onUserDisconnected(uid: string) {
    Logger.log('User disconnected: ' + uid);
    this.connectedUsers = this.connectedUsers.filter(value => (value.uid !== uid));
    this.dispatchConnectedUsersChanged();
  }

  private dispatchConnectedUsersChanged() {
    this.onConnectedUsersChanged.next(this.connectedUsers);
    this.syncService.onConnectedUsersChanged(this.connectedUsers);
  }

  /**
   * Nobody else is here yet: the next project starts from an empty list, in
   * the toolbar and the sync service alike. Only the field was emptied, so the
   * toolbar kept the last project's avatars until someone joined.
   */
  private resetConnectedUsers() {
    this.connectedUsers = [];
    this.dispatchConnectedUsersChanged();
  }

  private dispatchOpenSessionsChanged(sessionsOpen: number) {
    this.syncService.onOpenSessionsChanged(sessionsOpen);
  }

  onProjectRemoved(project: ProjectData) {
    if (this.currentProject && this.currentProject.project_id === project.project_id) {
      this.currentProject = null;
      this.closeProject();
    }
  }

  /**
   * The open project was deleted, or this account may no longer open it.
   * Every save would be refused from here on, so the canvas is left, with a
   * word on why, rather than letting work pile up that cannot be kept.
   */
  private leaveUnavailableProject(projectId: string) {
    if (this.currentProjectId !== projectId) {
      return;
    }
    // Nothing is written on the way out: there is nothing left to write to.
    this.currentProject = null;
    this.closeProject();
    openSnack(this.snackBar, this.strings.getString('project_no_longer_available'),
      this.strings.getString('dismiss'), ERROR_SNACK_DURATION, 'error');
    if (CommonUtil.inIframe()) {
      // An embed's visitor is told it may have been made private or deleted.
      this.router.navigateByUrl(embedUnavailableUrl('unavailable'), {skipLocationChange: true});
    } else {
      this.router.navigateByUrl(this.auth.isRealUser() ? '/dashboard/projects' : '/unauthorized');
    }
  }

  closeProject() {
    // A load still on its way stops where it is (see loadProject).
    this.loadToken++;
    clearTimeout(this.listSummaryTimer);
    // Each writes only for someone who may edit, from a map that finished
    // loading (canWriteSummary).
    this.updateProjectBounds();
    this.saveCounts();
    this.loadedProjectId = null;
    // Off the project's list of people working in it. Only going offline did
    // that, so collaborators kept seeing this account there after it left.
    this.disconnectUser();
    this.currentProject = null;
    this.initProjectEvents(null);
    this.syncService.setDataEventsEnabled(false);
    // Local only: switching projects must never write to the database.
    this.syncService.resetLocalState();
    // The session ends with the project, or it went on counting as one open
    // in another tab (initProjectSession) when the project was opened again.
    if (this.currentSessionId) {
      this.firebaseApi.getSessionsRef().query.ref.child(this.currentSessionId).remove()
        .catch(err => Logger.error(err));
    }
    this.setSessionId(null);
    if (this.sessionsSubscription) {
      this.sessionsSubscription.unsubscribe();
      this.sessionsSubscription = null;
    }
    if (this.projectSubscription) {
      this.projectSubscription.unsubscribe();
      this.projectSubscription = null;
    }
  }

  /**
   * Whether this session writes the project list's summary of the map (its
   * picture, counts, area and bounds): it may edit the project, and the map
   * finished loading. A viewer's writes were refused, or uploaded a picture
   * on every visit; and a map left while it was still loading, or that failed
   * to load, holds no shapes yet and recorded the project as empty.
   */
  private canWriteSummary(): boolean {
    const p = this.currentProject;
    return !!p && !!p.project_id && this.loadedProjectId === p.project_id && this.canEdit();
  }

  updateProjectBounds(){
    Logger.log('updateProjectBounds 1');
    const p = this.currentProject
    if (this.canWriteSummary()) {
      Logger.log('updateProjectBounds 2');
      const projectRef = this.firebaseApi.getProjectRef(p.project_id).ref;
      const pData = this.syncService.getGeometriesData();
      projectRef.update({
        bounds: ProjectUtils.getProjectBounds(pData),
        updated_on: Date.now()
      }).catch(err => Logger.error('Could not save the project bounds', err));
    }
  }

  saveCounts() {
    const cp = this.currentProject;
    if (this.canWriteSummary()) {
      const polygons = this.syncService.getPolygons();
      // Denormalized so the project list can show how much was measured — and
      // what it is worth — without reading every geometry document of every
      // project. Square metres is the canonical unit; each viewer converts to
      // their own on display. Counted as the canvas totals them: summing
      // every shape added each hole back in, and the area a line's closing
      // chord would enclose.
      const update: any = {
        updated_on: Date.now(),
        total_polygons: countedShapes(polygons).length,
        total_spots: this.syncService.getSpots().length,
        total_area: totalMeasuredArea(polygons)
      };

      // ProductsService injects this service, so resolving it at construction
      // would be a cycle. It is only needed at save time.
      const estimate = this.injector.get(ProductsService).getEstimateSummary();
      if (estimate) {
        // Null means the catalogue has not loaded, in which case writing zero
        // would clobber a figure that is actually correct.
        update.estimate_total = estimate.total;
        update.estimate_tax = estimate.tax;
        update.estimate_currency = estimate.currency;
        update.estimate_mixed_currency = estimate.mixed;
      }

      this.firebaseApi.getProjectRef(cp.project_id).update(update)
        .catch(err => Logger.error('Could not save the project counts', err));
    }
  }

  /** Brings the list's counts and bounds up to date when the shapes changed without them. */
  saveCountsIfStale() {
    const cp = this.currentProject;
    if (!this.canWriteSummary()) {
      return;
    }
    const polygons = this.syncService.getPolygons();
    const area = totalMeasuredArea(polygons);
    // The area too: totals saved while holes and lines still counted in it
    // were too large, with counts that may well have been right.
    const stale = (cp.total_polygons || 0) !== countedShapes(polygons).length
      || (cp.total_spots || 0) !== this.syncService.getSpots().length
      || Math.abs((cp.total_area || 0) - area) > Math.max(1, area * 0.001);
    if (stale || (!cp.bounds && this.syncService.hasGeometries())) {
      this.updateProjectBounds();
    }
    if (stale) {
      this.saveCounts();
    }
  }

  canAccessProject(project: ProjectData): boolean {

    let canAccess = false;
    if (CommonUtil.inIframe()) {
      canAccess = true;
    }
    if (!CommonUtil.inIframe()
      && (this.auth.isGod()
        || project.access_level !== 'private'
        || (project.members || []).includes(this.auth.getUserId)
        || project.creator_id === this.auth.getUserId
        || (this.auth.hasActiveEnterprisePlan() && (this.auth.currentUserInfo?.enterpriseUsers ?? []).includes(project.creator_id))
        // An Admin or Manager of the creator's organisation, as the rules
        // allow: the dashboard lists them every project of the organisation,
        // and opening a member's private one sent them to "unauthorized".
        || this.organization.isStaffFor(project.creator_id))) {
      canAccess = true;
    }
    return canAccess;
  }

  canEdit() {
    return this.syncService.canEdit();
  }

  isMember(){
    const proj = this.currentProject;
    return proj && (proj.members || []).includes(this.auth.getUserId);
  }

  isOwner(){
    const proj = this.currentProject;
    return proj && proj.creator_id === this.auth.getUserId;
  }

  private lockSession() {
    this.onSessionLocked.next(true);
  }

  private unlockSession() {
    this.onSessionLocked.next(false);
  }

  getRecentProjects(limit: number = 2): Promise<ProjectData[]> {
    return new Promise<ProjectData[]>((resolve, reject) => {
      if (this.projects) {
        // The cache is ordered by whatever sort the user picked on the
        // projects page — "recent" must mean last opened, so sort a copy.
        const projects = [...this.projects]
          .sort((a, b) => (b.last_open ?? b.updated_on ?? 0) - (a.last_open ?? a.updated_on ?? 0))
          .slice(0, limit);
        resolve(projects);
      } else {
        this.firebaseApi.getRecentProjects(limit).then(resolve).catch(reject);
      }
    });
  }

  openLoadFileChooser(openPopup: () => Promise<DialogResult>,
                      accept: string = '.mmp,.kml,.kmz,.gpx,.json,.geojson,.topojson,.mbtiles,image/x-png,image/jpeg,image/jpg,image/png') {
    if (!this.mayImport()) {
      return;
    }
    const inputElement = document.createElement('input');
    inputElement.type = 'file';
    inputElement.accept = accept;
    inputElement.multiple = true;
    inputElement.addEventListener('change', e => {
      const files: FileList = e.target['files'];
      this.loadFiles(Array.from(files || []), openPopup);
    });
    inputElement.dispatchEvent(new MouseEvent('click'));
  }

  loadFile(file: File, openPopup: () => Promise<DialogResult>) {
    return this.loadFiles([file], openPopup);
  }

  /**
   * Importing is editing. A Viewer or a view-only visitor could drop a file
   * on the map: its shapes showed and were reported as imported but never
   * saved, while layer files still went up to Storage. Every import path
   * (drop, the menus, the layers panel) comes through here. The demo keeps
   * its import, which, like everything in the demo, is never saved.
   */
  private mayImport(): boolean {
    if (this.syncService.canEdit() || this.syncService.isDemoMode()) {
      return true;
    }
    openSnack(this.snackBar, this.strings.getString('canvas_import_view_only'),
      this.strings.getString('dismiss'), ERROR_SNACK_DURATION, 'error');
    return false;
  }

  /**
   * Import one or more files, then say what came in.
   *
   * The data formats (.mmp, KML, KMZ, GPX, GeoJSON, TopoJSON) are imported by
   * the server, as for every app: they are named to it as one import (POST
   * /import/:id/batches, adding to the map or replacing it), uploaded one
   * after another, and read and written there on their own. A reload or a
   * closed page does not stop them, and everyone on the project follows them
   * (ImportJobsService: the chip by the project name). Pictures, placed by
   * hand, and MBTiles maps are added here; so is every file in the demo,
   * which saves nothing.
   *
   * Asked once whether to keep what is on the map. A Replace's restore point
   * and its clear are the server's too: the old map goes only once something
   * has come in. An image is placed by hand, so only one is taken.
   *
   * What came in, and what a Replace took away, is one undo step, recorded
   * once it has all arrived -- if this page is still on the project. Nothing
   * is held open meanwhile: an edit made while files go up is a step of its
   * own, and Undo works throughout.
   */
  async loadFiles(files: File[], openPopup: () => Promise<DialogResult>) {
    if (!this.mayImport()) {
      return;
    }
    const imageTypes = ['jpeg', 'jpg', 'png'];
    const extOf = (f: File) => FileUtil.getFileExtension(f.name).toLowerCase();
    let imageTaken = false;
    const queue = files.filter(f => {
      if (imageTypes.includes(extOf(f))) {
        if (imageTaken) {
          return false;
        }
        imageTaken = true;
      }
      return true;
    });
    if (!queue.length) {
      return;
    }
    const isData = (f: File) => FileLoader.DATA_EXTENSIONS.includes(extOf(f));
    const hasData = queue.some(isData);
    let keepCurrent = true;
    // Replacing the map is for the people who work on the project. A visitor
    // drawing through a shared link only ever adds to it.
    if (this.syncService.hasGeometries() && hasData && openPopup && this.syncService.canManageProject()) {
      const result = await openPopup();
      if (result === DialogResult.POSITIVE) {
        keepCurrent = false;
      } else if (result !== DialogResult.NEUTRAL) {
        return;
      }
    }

    const started = Date.now();
    const summary: ImportSummary = {items: 0, imported: false, changed: false, skipped: {}, failed: [],
      arrivals: [], notCleared: false};
    const generation = this.syncService.loadGeneration;
    const groupsBefore = this.syncService.groupsSnapshot();
    const serverFiles = this.syncService.isDemoMode() ? [] : queue.filter(isData);
    const run = serverFiles.length
      ? await this.startServerImport(serverFiles, keepCurrent ? 'add' : 'replace', summary, extOf, generation) : null;
    await this.importHere(queue.filter(file => !serverFiles.includes(file)), summary, extOf);
    if (run) {
      await this.finishServerImport(run, summary, extOf, groupsBefore, generation);
    }
    if (this.syncService.loadGeneration !== generation) {
      // Another project was opened meanwhile: what came in went into the one
      // the import was started in, whose notification says so.
      return;
    }
    const geometries = summary.arrivals.flatMap(arrival => arrival.geometries);
    // Everything that came in, and what a Replace took away, is one undo
    // step: Undo takes out what came in and puts back what was there. The
    // message's Undo is for that step alone, taken here with nothing in
    // between: an edit made while the files were read, map tiles, the demo
    // (which records nothing) are not it.
    const before = this.syncService.latestStep();
    this.syncService.beginUndoGroup();
    try {
      summary.arrivals.forEach(arrival => this.syncService.recordImport(arrival.changes));
    } finally {
      this.syncService.endUndoGroup();
    }
    const after = this.syncService.latestStep();
    const step = after !== before ? after : null;
    if (step) {
      summary.changed = true;
    }
    if (summary.arrivals.length) {
      // As the map is now -- after a Replace, the new one alone -- and fitted
      // to it only if the import was quick: one that took a while ends while
      // the person works.
      this.syncService.finishImport(geometries, Date.now() - started < QUICK_IMPORT_MS);
    }
    this.reportImport(summary, extOf, step);
  }

  /**
   * Names the files to the server as one import and uploads them, one after
   * another; the server reads each as it arrives. A Replace notes first what
   * it will take away, for Undo, and sends the saves on their way, for the
   * restore point the server takes. Null when the import could not start
   * (the files are in the summary's failures), was not wanted, or another
   * project was opened before it started: a Replace must not go on in a
   * project the person left.
   */
  private async startServerImport(files: File[], mode: ImportMode, summary: ImportSummary,
                                  extOf: (f: File) => string, generation: number): Promise<ServerImportRun | null> {
    const failAll = (error: (file: File) => any) => files.forEach(file => summary.failed.push({file, error: error(file)}));
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      failAll(file => new ImportError('offline', file.name, extOf(file)));
      return null;
    }
    const projectId = this.currentProjectId;
    const api = this.injector.get(ImportApiService);
    const left = () => this.syncService.loadGeneration !== generation;
    let started: ImportBatchResponse;
    let removing: any[] = [];
    // Something on screen until the import's chip shows: a Replace's restore point can take a while.
    this.syncService.loadingProgress = 0;
    try {
      if (mode === 'replace') {
        removing = this.syncService.replacedChanges(this.syncService.planClear(everything()));
        await this.syncService.flushBeforeReplace();
        if (left()) {
          return null;
        }
      }
      const request: ImportBatchRequest = {
        mode, files: files.map(file => ({name: file.name, size: file.size})),
        language: this.strings.getLanguage(), style: this.importStyle(),
      };
      try {
        started = await api.createBatch(projectId, request);
      } catch (e) {
        if (!(e && e.msg && e.msg.errorCode === 'import_restore_point_failed')) {
          failAll(file => ProjectService.importErrorOf(e, file, extOf(file)));
          return null;
        }
        // The old map would go with no restore point to bring it back: asked, as Clear Map asks.
        this.syncService.loadingProgress = -1;
        if (left() || !(await this.injector.get(PopupsService).confirmClearWithoutRestorePoint()) || left()) {
          return null;
        }
        this.syncService.loadingProgress = 0;
        try {
          started = await api.createBatch(projectId, {...request, withoutRestorePoint: true});
        } catch (again) {
          failAll(file => ProjectService.importErrorOf(again, file, extOf(file)));
          return null;
        }
      }
    } finally {
      this.syncService.loadingProgress = -1;
    }
    this.importJobs.adopt(started.batch.id);
    const uploads: ImportUploads = {stopped: false, task: null};
    this.importUploads.set(started.batch.id, uploads);
    const uploadErrors = new Map<string, any>();
    try {
      for (const [index, job] of started.jobs.entries()) {
        if (!job.path) {
          // Refused at once (too large, a kind not imported): the list and the message say why.
          continue;
        }
        if (uploads.stopped) {
          break;
        }
        try {
          await this.uploadForImport(projectId, job.id, job.path, files[index], uploads);
        } catch (e) {
          Logger.error('Import: upload failed', files[index].name, e);
          uploadErrors.set(job.id, ProjectService.uploadErrorOf(e, files[index], extOf(files[index])));
          api.reportUpload(projectId, job.id, {state: 'failed'}).catch(err => Logger.error('Import: upload failure not reported', err));
        }
      }
    } finally {
      this.importUploads.delete(started.batch.id);
    }
    return {projectId, batchId: started.batch.id, files, removing, uploadErrors};
  }

  /**
   * Stops an import of one's own: the files still to upload, or uploading,
   * go no further (the server records them as stopped); those already with
   * the server are read all the same. From the page that started it, or any
   * other of the same person's.
   */
  stopImport(batch: ImportBatchView) {
    const uploads = this.importUploads.get(batch.id);
    if (uploads) {
      uploads.stopped = true;
      uploads.task?.cancel();
    }
    const projectId = this.currentProjectId;
    const api = this.injector.get(ImportApiService);
    batch.files.filter(file => ['waiting', 'uploading', 'interrupted'].includes(file.status))
      .forEach(file => api.reportUpload(projectId, file.id, {state: 'failed'})
        .catch(e => Logger.error('Import: stop not reported', e)));
  }

  /**
   * Waits for the server to finish an import, then gathers what each file
   * brought into the summary, and -- still on its project -- what arrived
   * (and what a Replace took away) for the undo step.
   */
  private async finishServerImport(run: ServerImportRun, summary: ImportSummary, extOf: (f: File) => string,
                                   groupsBefore: {[id: string]: any}, generation: number) {
    let batch: any;
    let results: {job: any, geometries: string[]}[];
    try {
      batch = await this.importJobs.whenFinished(run.projectId, run.batchId);
      results = await this.importJobs.filesOf(run.projectId, run.batchId);
    } catch (e) {
      Logger.error('Import: the import could not be followed', e);
      run.files.forEach(file => summary.failed.push({file, error: e}));
      return;
    }
    const here = this.syncService.loadGeneration === generation;
    for (const {job} of results) {
      const file = run.files[job.order] || run.files[0];
      if (job.status === 'done' && job.result) {
        summary.imported = true;
        summary.items += job.result.items || 0;
        const skipped = job.result.skipped || {};
        Object.keys(skipped).forEach(k => summary.skipped[k] = (summary.skipped[k] || 0) + skipped[k]);
        // The project settings an .mmp gave it, shown at once.
        const settings = job.result.project;
        if (here && settings && Object.keys(settings).length && this.currentProject) {
          Object.assign(this.currentProject, settings);
        }
        // Which formats people bring in, for the admin dashboard.
        this.analyticsEvents.record('file_imported', {format: extOf(file), items: job.result.items || 0}, run.projectId);
      } else {
        summary.failed.push({file, error: run.uploadErrors.get(job.id) || ProjectService.jobErrorOf(job.error, file, extOf(file))});
      }
    }
    summary.notCleared = !!(batch.result && batch.result.notCleared);
    if (!here) {
      return;
    }
    const created = {
      geometries: results.flatMap(r => r.geometries),
      groups: results.flatMap(r => (r.job.result && r.job.result.groups) || []),
      layers: results.flatMap(r => (r.job.result && r.job.result.layers) || []),
    };
    let cleared = null;
    if (batch.result && batch.result.cleared) {
      cleared = await this.importJobs.clearedOf(run.projectId, run.batchId).catch(e => {
        Logger.error('Import: what the Replace removed could not be read', e);
        return null;
      });
    }
    const arrival = await this.syncService.awaitServerImport(created, groupsBefore, cleared, run.removing);
    if (arrival) {
      summary.arrivals.push(arrival);
    }
  }

  /**
   * Pictures and map tiles, and in the demo every file: added here, one after
   * another. One that fails does not stop the ones after it.
   */
  private async importHere(files: File[], summary: ImportSummary, extOf: (f: File) => string) {
    for (const file of files) {
      try {
        let items = 0;
        if (FileLoader.DATA_EXTENSIONS.includes(extOf(file))) {
          const r = await this.previewIntoDemo(file, extOf(file));
          summary.imported = true;
          items = r.items;
          summary.items += r.items;
          Object.keys(r.skipped || {}).forEach(k => summary.skipped[k] = (summary.skipped[k] || 0) + r.skipped[k]);
          if (r.items > 0) {
            // The demo's map took the file itself.
            summary.changed = true;
          }
        } else {
          await this.syncService.loadFile(file);
          if (extOf(file) === 'mbtiles') {
            // Map tiles became a layer, a step of its own.
            summary.changed = true;
          }
        }
        // Which formats people bring in, for the admin dashboard. Images and
        // map tiles become layers and return nothing to count, but arrived.
        this.analyticsEvents.record('file_imported', {format: extOf(file), items}, this.currentProject?.project_id);
      } catch (e) {
        Logger.error('Import failed', file.name, e);
        summary.failed.push({file, error: e});
      }
    }
  }

  /** What a file's job records as its failure, as the error the message names. */
  private static jobErrorOf(code: string, file: File, extension: string): any {
    const mapped = code === 'import_too_large' ? 'too_large' : code === 'import_invalid_file' ? 'invalid_file'
      : code === 'import_unsupported' ? 'unsupported' : code === 'project_unauthorized_access' ? 'not_allowed' : null;
    // Interrupted, not uploaded, failed on the server: not the file's fault.
    return mapped ? new ImportError(mapped, file.name, extension) : new Error(code || 'import_failed');
  }

  /**
   * The most map data a file of this kind may hold, in MB: the server's
   * limits. XML takes far more memory to read than JSON does.
   */
  private static importLimitMb(extension: string): number {
    return ['json', 'geojson', 'topojson'].includes(extension) ? FileLoader.MAX_JSON_MB : FileLoader.MAX_XML_MB;
  }

  /**
   * Uploads a file where its job says: that it starts, how far it got every
   * UPLOAD_REPORT_MS -- everyone on the project sees it, and the server knows
   * the page, and the files waiting after this one, are still there -- and
   * that it is there once done, which sets the server reading it. A file the
   * server has given up on, or one stopped here, goes no further. The import
   * folder is the server's to read and empty: no browser may read it.
   */
  private uploadForImport(projectId: string, jobId: string, path: string, file: File, uploads: ImportUploads): Promise<void> {
    const api = this.injector.get(ImportApiService);
    return new Promise<void>((resolve, reject) => {
      let uploaded = 0;
      const task = this.firebaseApi.storage().ref(path).put(file, {contentType: file.type || 'application/octet-stream'});
      uploads.task = task;
      const report = () => api.reportUpload(projectId, jobId, {state: 'uploading', uploaded}).then(reply => {
        // Given up on meanwhile (stopped, or the server heard nothing for too long): no use sending the rest.
        if (reply && reply.status !== 'uploading') {
          task.cancel();
        }
      }, e => Logger.error('Import: upload progress not reported', e));
      report();
      const timer = setInterval(report, UPLOAD_REPORT_MS);
      const done = () => {
        clearInterval(timer);
        uploads.task = null;
      };
      task.on('state_changed', snapshot => {
        uploaded = snapshot.bytesTransferred;
        this.importJobs.uploadProgress(jobId, uploaded);
      }, error => {
        done();
        reject(error);
      }, () => {
        done();
        this.importJobs.uploadProgress(jobId, file.size);
        ProjectService.reportUploaded(api, projectId, jobId).then(status => status === 'queued'
          ? resolve() : reject(new Error('import_upload_refused')), reject);
      });
    });
  }

  /** That an upload is there, tried again a couple of times: one lost request failed a whole file. */
  private static async reportUploaded(api: ImportApiService, projectId: string, jobId: string): Promise<string> {
    for (let attempt = 1; ; attempt++) {
      try {
        return (await api.reportUpload(projectId, jobId, {state: 'uploaded'})).status;
      } catch (e) {
        if (attempt >= 3 || (e && e.msg && e.msg.errorCode)) {
          throw e;
        }
        await new Promise(resolve => setTimeout(resolve, attempt * 2000));
      }
    }
  }

  /** A failed upload as the import's own error where it has a reason to give; anything else as it is. */
  private static uploadErrorOf(e: any, file: File, extension: string): any {
    const code = e && e.code;
    if (code === 'storage/retry-limit-exceeded') {
      return new ImportError('offline', file.name, extension);
    }
    if (code === 'storage/unauthorized' || code === 'storage/unauthenticated') {
      return new ImportError('not_allowed', file.name, extension);
    }
    return e;
  }

  /**
   * The demo's import: it saves nothing, so the server reads the file without
   * adding it anywhere (POST /import/preview) and the map takes what comes
   * back. Signed in anonymously for the call, as a shared link is. Reference
   * layers and pictures need files of their own and are left out.
   */
  private async previewIntoDemo(file: File, extension: string): Promise<FileImportResult> {
    if (file.size > MAX_PREVIEW_BYTES) {
      throw new ImportError('too_large', file.name, extension);
    }
    let preview: ImportPreview;
    this.syncService.loadingProgress = 0;
    try {
      // FirebaseApi's own user, not AngularFireAuth's currentUser: that one is
      // a promise, always there, so the demo never signed in.
      if (!this.firebaseApi.getCurrentUser()) {
        const credential = await this.firebaseApi.auth().signInAnonymously();
        // FirebaseApi hears of the session a moment later; the call needs its token now.
        if (!this.firebaseApi.getCurrentUser() && credential && credential.user) {
          this.firebaseApi.currentUser = credential.user;
        }
      }
      preview = await this.injector.get(ImportPreviewApiService).preview(file.name, await ProjectService.base64Of(file),
        this.strings.getLanguage(), this.importStyle());
    } catch (e) {
      throw ProjectService.importErrorOf(e, file, extension);
    } finally {
      this.syncService.loadingProgress = -1;
    }
    // What the demo leaves out of a file it read whole: its reference layers
    // and pictures, which would need files of their own.
    const skipped = {...preview.skipped};
    const leftOut = preview.layers.length + (skipped.picture || 0);
    delete skipped.picture;
    if (leftOut) {
      skipped.demo = leftOut;
    }
    const parsed: FileImport = {
      geometries: preview.geometries.map(doc => doc.type === GeometryType.TYPE_POLYGON
        ? GeometryConverter.dataToPolygon(doc) : GeometryConverter.dataToSpot(doc)),
      groups: preview.groups.map(group => Object.assign(new GeoGroupData(), group)),
      layers: [],
      skipped,
      project: preview.project || undefined,
    };
    await this.syncService.loadFile(file, parsed);
    const items = parsed.geometries.filter(geo => !(geo instanceof Polygon && geo.isHole)).length;
    return {items, skipped, groups: []};
  }

  /** A file's bytes as base64, for the demo's preview. */
  private static base64Of(file: Blob): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result).replace(/^data:[^,]*,/, ''));
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }

  /** How the person importing draws: what a file's unstyled shapes get. */
  private importStyle(): ImportStyle {
    const s = SettingsService.settings;
    const l = SettingsService.localSettings;
    return {
      lineColor: s.lineColor,
      lineWidth: s.lineWidth,
      lineStyle: s.lineStyle,
      fillColor: s.fillColor,
      fillTransparency: s.fillOpacity,
      showDistances: s.showDistanceLabel,
      showPolygonName: l.showPolygonNameLabel,
      showPolygonArea: l.showPolygonAreaLabel,
      showPolygonPerimeter: l.showPolygonPerimeterLabel,
      showSpotName: l.showSpotNameLabel,
      spotIconColor: l.defaultSpotIconColor,
      default3dAltitude: Number(l.default3dAltitude) || 0,
    };
  }

  /** The server's refusal as the import's own error, which the message names; anything else as it is. */
  private static importErrorOf(e: any, file: File, extension: string): any {
    const code = e && e.msg && e.msg.errorCode;
    const mapped = code === 'import_too_large' ? 'too_large' : code === 'import_invalid_file' ? 'invalid_file'
      : code === 'import_unsupported' ? 'unsupported' : code === 'project_unauthorized_access' ? 'not_allowed' : null;
    return mapped ? new ImportError(mapped, file.name, extension) : e;
  }

  /**
   * One message for the whole import: what came in, what was left out, and
   * every file that could not be imported, with an Undo whenever the import
   * made a step -- a Replace that cleared the map included. The Undo takes
   * back that step only, while it is still the newest.
   */
  private reportImport(summary: ImportSummary, extOf: (f: File) => string, step: object | null) {
    const failed = summary.failed;
    // An image alone is placed by hand next, and map tiles show themselves.
    if (!summary.imported && !failed.length) {
      return;
    }
    const sentences: string[] = [];
    const skipped = summary.skipped;
    const parts = Object.keys(skipped).filter(k => skipped[k] > 0)
      .map(k => this.strings.getString('import_skip_' + k, undefined, skipped[k].toLocaleString()));
    if (summary.imported) {
      sentences.push(summary.items === 1
        ? this.strings.getString('import_done_one')
        : summary.items
        ? this.strings.getString('import_done', undefined, summary.items.toLocaleString())
        : this.strings.getString('import_nothing'));
      if (parts.length) {
        sentences.push(this.strings.getString('import_not_imported', undefined, parts.join(', ')));
      }
      if (summary.notCleared) {
        sentences.push(this.strings.getString('import_replace_not_cleared'));
      }
    }
    if (failed.length > 2) {
      sentences.push(this.strings.getString('import_failed_many', undefined, failed.length.toLocaleString(),
        failed.map(f => f.file.name).join(', ')));
    } else {
      failed.forEach(f => sentences.push(this.importFailureText(f, extOf)));
    }

    const kind = failed.length && !summary.imported ? 'error'
      : failed.length || parts.length || !summary.items || summary.notCleared ? 'warning' : 'success';
    const duration = kind === 'success' ? 5000 : kind === 'error' && !summary.changed ? ERROR_SNACK_DURATION : 12000;
    const ref = openSnack(this.snackBar, [...new Set(sentences)].join(' '),
      this.strings.getString(step ? 'undo' : 'dismiss'), duration, kind);
    if (step) {
      // The clear and everything added are one step; only that step is taken back.
      ref.onAction().subscribe(() => this.syncService.undoIfStillLatest(step));
    }
  }

  private importFailureText(failure: ImportFailure, extOf: (f: File) => string): string {
    const {file, error} = failure;
    if (error instanceof ImportError) {
      if (error.code === 'too_large') {
        const extension = extOf(file);
        const limit = this.syncService.isDemoMode() ? MAX_PREVIEW_BYTES / MB
          : extension === 'kmz' && file.size > MAX_KMZ_BYTES ? MAX_KMZ_BYTES / MB : ProjectService.importLimitMb(extension);
        return this.strings.getString('import_too_large', undefined, file.name, limit.toLocaleString());
      }
      if (error.code === 'offline') {
        return this.strings.getString('import_offline', undefined, file.name);
      }
      if (error.code === 'not_allowed') {
        return this.strings.getString('canvas_import_view_only');
      }
      if (error.code === 'vector_tiles') {
        // A sound MBTiles file, but of vector tiles, which the map cannot draw.
        return this.strings.getString('import_mbtiles_vector', undefined, file.name);
      }
      const key = error.code === 'unsupported' ? 'import_unsupported' : 'import_failed';
      return this.strings.getString(key, undefined, file.name, extOf(file).toUpperCase());
    }
    // Read, but not added: not a fault of the file.
    return this.strings.getString('import_failed_unknown', undefined, file.name);
  }

  getProjectBounds() {
    const p = this.currentProject;
    if(p){
      const bounds = new google.maps.LatLngBounds();
      bounds.extend({lat: p.bounds.minLat, lng: p.bounds.minLng});
      bounds.extend({lat: p.bounds.maxLat, lng: p.bounds.maxLng});
      return bounds;
    }
    return null;
  }
}
